import { TableView } from '@components/table/types';

import { request } from '../request';

export type TableViewApi = {
  shortId: string;
  name: string;
  isCollaborative: boolean;
  config: Omit<TableView, 'id' | 'name' | 'isCollaborative' | 'isOwn'>;
  isOwn: boolean;
  dateCreated: string;
  dateUpdated: string;
};

export type TableViewSettingsResponse = {
  views: TableViewApi[];
  selectedView: string | null;
  viewOrder: string[];
};

export type TableType = 'journey' | 'company-travellers';

type TableViewQueryParams = {
  tableType: TableType;
  tableObjectShortId?: string;
};

const buildQueryString = (params: TableViewQueryParams) => {
  const queryString = new URLSearchParams({ table_type: params.tableType });
  if (params.tableObjectShortId) {
    queryString.set('table_object_short_id', params.tableObjectShortId);
  }
  return queryString.toString();
};

export const loadTableViewSettings = async (params: TableViewQueryParams) => {
  const response = await request.get<TableViewSettingsResponse>(
    `v1/table-views/?${buildQueryString(params)}`,
  );
  return response.data;
};

export type CreateTableViewData = {
  name: string;
  isCollaborative?: boolean;
  config: Record<string, any>;
  tableType: TableType;
  tableObjectShortId?: string;
};

export const createTableView = async (data: CreateTableViewData) => {
  const response = await request.post<TableViewApi>('v1/table-views/', {
    name: data.name,
    is_collaborative: data.isCollaborative ?? false,
    config: data.config,
    table_type: data.tableType,
    table_object_short_id: data.tableObjectShortId,
  });
  return response.data;
};

export type UpdateTableViewData = {
  name?: string;
  isCollaborative?: boolean;
  config?: Record<string, any>;
};

export const updateTableView = async (shortId: string, data: UpdateTableViewData) => {
  const payload: Record<string, any> = {};
  if (data.name !== undefined) payload.name = data.name;
  if (data.isCollaborative !== undefined) payload.is_collaborative = data.isCollaborative;
  if (data.config !== undefined) payload.config = data.config;

  const response = await request.patch<TableViewApi>(`v1/table-views/${shortId}/`, payload);
  return response.data;
};

export const deleteTableView = async (shortId: string) => {
  await request.delete(`v1/table-views/${shortId}/`);
};

export type UpdateTableViewPreferenceData = {
  selectedView?: string | null;
  viewOrder?: string[];
};

export const updateTableViewPreferences = async (
  params: TableViewQueryParams,
  data: UpdateTableViewPreferenceData,
) => {
  const payload: Record<string, any> = {};
  if (data.selectedView !== undefined) payload.selected_view = data.selectedView;
  if (data.viewOrder !== undefined) payload.view_order = data.viewOrder;

  const response = await request.patch<{ selectedView: string | null; viewOrder: string[] }>(
    `v1/table-views/preferences/?${buildQueryString(params)}`,
    payload,
  );
  return response.data;
};
